import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    TextField,
    Stack,
    Paper,
    InputAdornment,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    FileDownload as FileDownloadIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const CryptoQuotesScreenerFilterSystem = ({ onSearch, onClear, onExport, showExport = false }) => {
    const theme = useTheme();

    const predefinedRanges = {
        marketCap: {
            above: [
                { label: 'Above 1M', value: 1000000 },
                { label: 'Above 10M', value: 10000000 },
                { label: 'Above 100M', value: 100000000 },
                { label: 'Above 500M', value: 500000000 },
                { label: 'Above 1B', value: 1000000000 },
                { label: 'Above 10B', value: 10000000000 },
                { label: 'Above 50B', value: 50000000000 },
                { label: 'Above 100B', value: 100000000000 },
                { label: 'Above 300B', value: 300000000000 },
                { label: 'Above 500B', value: 500000000000 },
                { label: 'Above 1T', value: 1000000000000 }
            ],
            below: [
                { label: 'Under 1M', value: 1000000 },
                { label: 'Under 10M', value: 10000000 },
                { label: 'Under 100M', value: 100000000 },
                { label: 'Under 500M', value: 500000000 },
                { label: 'Under 1B', value: 1000000000 },
                { label: 'Under 10B', value: 10000000000 },
                { label: 'Under 50B', value: 50000000000 },
                { label: 'Under 100B', value: 100000000000 },
                { label: 'Under 300B', value: 300000000000 },
                { label: 'Under 500B', value: 500000000000 },
                { label: 'Under 1T', value: 1000000000000 }
            ]
        },
        price: {
            above: [
                { label: 'Above $0.1', value: 0.1 },
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $0.1', value: 0.1 },
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        },
        changesPercentage: {
            above: [
                { label: 'Above 1%', value: 1 },
                { label: 'Above 5%', value: 5 },
                { label: 'Above 10%', value: 10 },
                { label: 'Above 20%', value: 20 },
                { label: 'Above 50%', value: 50 },
                { label: 'Above -1%', value: -1 },
                { label: 'Above -5%', value: -5 },
                { label: 'Above -10%', value: -10 },
                { label: 'Above -20%', value: -20 },
                { label: 'Above -50%', value: -50 }
            ],
            below: [
                { label: 'Under 1%', value: 1 },
                { label: 'Under 5%', value: 5 },
                { label: 'Under 10%', value: 10 },
                { label: 'Under 20%', value: 20 },
                { label: 'Under 50%', value: 50 },
                { label: 'Under -1%', value: -1 },
                { label: 'Under -5%', value: -5 },
                { label: 'Under -10%', value: -10 },
                { label: 'Under -20%', value: -20 },
                { label: 'Under -50%', value: -50 }
            ]
        },
        change: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $80', value: 80 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $150', value: 150 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $80', value: 80 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $150', value: 150 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        },
        dayLow: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $80', value: 80 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $150', value: 150 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $80', value: 80 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $150', value: 150 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        },
        dayHigh: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $80', value: 80 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $150', value: 150 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $80', value: 80 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $150', value: 150 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        },
        yearHigh: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $80', value: 80 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $150', value: 150 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $80', value: 80 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $150', value: 150 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        },
        yearLow: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $80', value: 80 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $150', value: 150 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $80', value: 80 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $150', value: 150 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        },
        volume: {
            above: [
                { label: 'Above 1M', value: 1000000 },
                { label: 'Above 10M', value: 10000000 },
                { label: 'Above 100M', value: 100000000 },
                { label: 'Above 500M', value: 500000000 },
                { label: 'Above 1B', value: 1000000000 },
                { label: 'Above 10B', value: 10000000000 },
                { label: 'Above 50B', value: 50000000000 },
                { label: 'Above 100B', value: 100000000000 }
            ],
            below: [
                { label: 'Under 1M', value: 1000000 },
                { label: 'Under 10M', value: 10000000 },
                { label: 'Under 100M', value: 100000000 },
                { label: 'Under 500M', value: 500000000 },
                { label: 'Under 1B', value: 1000000000 },
                { label: 'Under 10B', value: 10000000000 },
                { label: 'Under 50B', value: 50000000000 },
                { label: 'Under 100B', value: 100000000000 }
            ]
        },
        avgVolume: {
            above: [
                { label: 'Above 1M', value: 1000000 },
                { label: 'Above 10M', value: 10000000 },
                { label: 'Above 100M', value: 100000000 },
                { label: 'Above 500M', value: 500000000 },
                { label: 'Above 1B', value: 1000000000 },
                { label: 'Above 10B', value: 10000000000 },
                { label: 'Above 50B', value: 50000000000 },
                { label: 'Above 100B', value: 100000000000 }
            ],
            below: [
                { label: 'Under 1M', value: 1000000 },
                { label: 'Under 10M', value: 10000000 },
                { label: 'Under 100M', value: 100000000 },
                { label: 'Under 500M', value: 500000000 },
                { label: 'Under 1B', value: 1000000000 },
                { label: 'Under 10B', value: 10000000000 },
                { label: 'Under 50B', value: 50000000000 },
                { label: 'Under 100B', value: 100000000000 }
            ]
        },
        open: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $80', value: 80 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $150', value: 150 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $80', value: 80 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $150', value: 150 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        },
        previousClose: {
            above: [
                { label: 'Above $1', value: 1 },
                { label: 'Above $5', value: 5 },
                { label: 'Above $10', value: 10 },
                { label: 'Above $20', value: 20 },
                { label: 'Above $50', value: 50 },
                { label: 'Above $80', value: 80 },
                { label: 'Above $100', value: 100 },
                { label: 'Above $150', value: 150 },
                { label: 'Above $200', value: 200 },
                { label: 'Above $500', value: 500 },
                { label: 'Above $1000', value: 1000 }
            ],
            below: [
                { label: 'Under $1', value: 1 },
                { label: 'Under $5', value: 5 },
                { label: 'Under $10', value: 10 },
                { label: 'Under $20', value: 20 },
                { label: 'Under $50', value: 50 },
                { label: 'Under $80', value: 80 },
                { label: 'Under $100', value: 100 },
                { label: 'Under $150', value: 150 },
                { label: 'Under $200', value: 200 },
                { label: 'Under $500', value: 500 },
                { label: 'Under $1000', value: 1000 }
            ]
        }
    };

    const [availableFilters, setAvailableFilters] = useState([]);
    const [activeFilters, setActiveFilters] = useState([]);
    const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilters, setSelectedFilters] = useState(new Set());

    useEffect(() => {
        setAvailableFilters([
            { id: 'price', label: 'Price', type: 'range', hasPresetRanges: true },
            { id: 'marketCap', label: 'Market Cap', type: 'range', hasPresetRanges: true },
            { id: 'changesPercentage', label: 'Changes Percentage', type: 'range', hasPresetRanges: true },
            { id: 'change', label: 'Change', type: 'range', hasPresetRanges: true },
            { id: 'dayLow', label: 'Day Low', type: 'range', hasPresetRanges: true },
            { id: 'dayHigh', label: 'Day High', type: 'range', hasPresetRanges: true },
            { id: 'yearHigh', label: 'Year High', type: 'range', hasPresetRanges: true },
            { id: 'yearLow', label: 'Year Low', type: 'range', hasPresetRanges: true },
            { id: 'volume', label: 'Volume', type: 'range', hasPresetRanges: true },
            { id: 'avgVolume', label: 'Average Volume', type: 'range', hasPresetRanges: true },
            { id: 'open', label: 'Open', type: 'range', hasPresetRanges: true },
            { id: 'previousClose', label: 'Previous Close', type: 'range', hasPresetRanges: true },
        ]);
    }, []);

    const toggleFilter = (filter) => {
        const newSelectedFilters = new Set(selectedFilters);

        if (newSelectedFilters.has(filter.id)) {
            newSelectedFilters.delete(filter.id);
            setActiveFilters(activeFilters.filter(f => f.id !== filter.id));
        } else {
            newSelectedFilters.add(filter.id);
            if (!activeFilters.find(f => f.id === filter.id)) {
                const newFilter = {
                    ...filter,
                    values: filter.type === 'range' ? { min: '', max: '' } : ''
                };
                setActiveFilters([...activeFilters, newFilter]);
            }
        }

        setSelectedFilters(newSelectedFilters);
    };

    const handleFilterValueChange = (filterId, value, isMin) => {
        setActiveFilters(activeFilters.map(filter => {
            if (filter.id === filterId) {
                if (filter.type === 'range') {
                    return {
                        ...filter,
                        values: {
                            ...filter.values,
                            [isMin ? 'min' : 'max']: value
                        }
                    };
                }
                return { ...filter, values: value };
            }
            return filter;
        }));
    };

    const handleSearch = () => {
        const filterData = {
            priceMoreThan: '',
            priceLowerThan: '',
            changesPercentageMoreThan: '',
            changesPercentageLowerThan: '',
            changeMoreThan: '',
            changeLowerThan: '',
            dayLowMoreThan: '',
            dayLowLowerThan: '',
            dayHighMoreThan: '',
            dayHighLowerThan: '',
            yearHighMoreThan: '',
            yearHighLowerThan: '',
            yearLowMoreThan: '',
            yearLowLowerThan: '',
            marketCapMoreThan: '',
            marketCapLowerThan: '',
            volumeMoreThan: '',
            volumeLowerThan: '',
            avgVolumeMoreThan: '',
            avgVolumeLowerThan: '',
            openMoreThan: '',
            openLowerThan: '',
            previousCloseMoreThan: '',
            previousCloseLowerThan: ''
        };

        activeFilters.forEach(filter => {
            if (filter.type === 'range') {
                if (filter.values.min !== '') filterData[`${filter.id}MoreThan`] = filter.values.min;
                if (filter.values.max !== '') filterData[`${filter.id}LowerThan`] = filter.values.max;
            }
        });

        onSearch(filterData);
    };

    const handleClear = () => {
        setActiveFilters([]);
        setSelectedFilters(new Set());
        onClear();
    };

    const renderRangeFilter = (filter) => {
        const ranges = predefinedRanges[filter.id];

        if (ranges) {
            return (
                <Stack spacing={1} sx={{ mt: 1 }}>
                    <FormControl fullWidth size="small">
                        <Select
                            value={filter.values?.min || ''}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, true)}
                            displayEmpty
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.mode === 'dark'
                                        ? theme.palette.text.secondary
                                        : theme.palette.text.disabled,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.text.primary,
                                },
                                '& .MuiSelect-select': {
                                    color: theme.palette.text.primary,
                                },
                                '& .MuiSelect-select.MuiSelect-select': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            <MenuItem value="" sx={{
                                color: theme.palette.text.primary,
                                opacity: 0.7
                            }}>
                                Select minimum
                            </MenuItem>
                            {ranges.above.map((range) => (
                                <MenuItem
                                    key={range.label}
                                    value={range.value}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <Select
                            value={filter.values?.max || ''}
                            onChange={(e) => handleFilterValueChange(filter.id, e.target.value, false)}
                            displayEmpty
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.mode === 'dark'
                                        ? theme.palette.text.secondary
                                        : theme.palette.text.disabled,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.text.primary,
                                },
                                '& .MuiSelect-select': {
                                    color: theme.palette.text.primary,
                                },
                                '& .MuiSelect-select.MuiSelect-select': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            <MenuItem value="" sx={{
                                color: theme.palette.text.primary,
                                opacity: 0.7
                            }}>
                                Select maximum
                            </MenuItem>
                            {ranges.below.map((range) => (
                                <MenuItem
                                    key={range.label}
                                    value={range.value}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            );
        }

        return (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                    size="small"
                    placeholder="Min"
                    value={filter.values.min}
                    onChange={(e) => handleFilterValueChange(filter.id, e.target.value, true)}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: theme.palette.text.primary,
                            '& fieldset': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme.palette.text.secondary,
                        },
                    }}
                />
                <TextField
                    size="small"
                    placeholder="Max"
                    value={filter.values.max}
                    onChange={(e) => handleFilterValueChange(filter.id, e.target.value, false)}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: theme.palette.text.primary,
                            '& fieldset': {
                                borderColor: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme.palette.text.secondary,
                        },
                    }}
                />
            </Stack>
        );
    };

    const filteredOptions = availableFilters.filter(filter =>
        filter.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ mb: 3 }}>
            <Paper
                variant='stats'
                sx={{
                    p: 2,
                    borderRadius: 1
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{
                        color: theme.palette.text.primary
                    }}>
                        {activeFilters.length} Filters
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddFilterOpen(true)}
                        variant="contained"
                    >
                        Add Filters
                    </Button>
                </Stack>

                {activeFilters.length > 0 && (
                    <Grid container spacing={2}>
                        {activeFilters.map((filter) => (
                            <Grid item xs={12} sm={6} md={4} key={filter.id}>
                                <Paper
                                    variant='stats'
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="subtitle2" sx={{
                                            flex: 1,
                                            color: theme.palette.text.primary
                                        }}>
                                            {filter.label}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleFilter(filter)}
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                    {renderRangeFilter(filter)}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Grid container spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    {showExport && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                onClick={onExport}
                            >
                                Export
                            </Button>
                        </Grid>
                    )}
                    <Grid item>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSearch}
                            startIcon={<SearchIcon />}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>

                <Dialog
                    open={isAddFilterOpen}
                    onClose={() => setIsAddFilterOpen(false)}
                    maxWidth="xs"
                    fullWidth
                    sx={{
                        '& .MuiDialog-container': {
                            backdropFilter: 'blur(4px)',
                        },
                        '& .MuiPaper-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(13, 34, 55, 0.95)'
                                : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            backgroundColor: 'transparent',
                            borderBottom: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            padding: '16px 24px'
                        }}
                    >
                        Add Filters
                        <IconButton
                            onClick={() => setIsAddFilterOpen(false)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            backgroundColor: 'transparent',
                            padding: '20px 24px',
                        }}
                    >
                        <TextField
                            fullWidth
                            placeholder="Search filters..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? theme.palette.text.secondary
                                            : theme.palette.text.disabled,
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: theme.palette.text.secondary,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <List sx={{ width: '100%' }}>
                            {filteredOptions.map((filter) => (
                                <ListItem
                                    key={filter.id}
                                    button
                                    onClick={() => toggleFilter(filter)}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    <ListItemText primary={filter.label} />
                                    {selectedFilters.has(filter.id) && (
                                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                                            <CheckCircleIcon color="success" />
                                        </ListItemIcon>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            backgroundColor: 'transparent',
                            borderTop: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'}`,
                            padding: '16px 24px',
                        }}
                    >
                        <Button
                            onClick={() => setIsAddFilterOpen(false)}
                            variant="contained"
                        >
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default CryptoQuotesScreenerFilterSystem;